import React, { useState, useMemo } from 'react';
import { type FproDosDocFileJsonldDocumentfileRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { getFileExtension } from '@europrocurement/l2d-utils';
import { AxiosResponse } from 'axios';
import { Checkbox, PdfViewer, type ItemActionsProps } from '@europrocurement/flexy-components';
import { type CreatedLiasseType } from '../../../providers/types';
import DocumentItem from '../../detail/DocumentItem/DocumentItem';
import { DocumentExtension } from '../../../hooks/useDossierDocumentFilesApi';

export type LiasseCheckerFormProps = {
    createdLiasses: CreatedLiasseType[];
    getStatusIdentifier: (statusId: number) => 'pending' | 'error' | 'success' | undefined;
    getFichierContent: (fichierId: number) => Promise<AxiosResponse<void>>;
    handleSubmit: (filesToRemove: FproDosDocFileJsonldDocumentfileRead[]) => void;
    isSubmitting: boolean;
};

type DocItem = {
    fileName: string;
    uploadable: boolean;
    uploading: boolean;
    id?: number;
    fichierId?: number;
    type?: DocumentExtension;
    label: string;
    status: 'pending' | 'error' | 'success' | undefined;
    feedback?: string;
    documentTypeId?: number;
    file?: FproDosDocFileJsonldDocumentfileRead;
    itemActions?: ItemActionsProps;
};
type DocPreview = {
    fileName: string;
    content: Blob;
};

const LiasseCheckerForm: React.FunctionComponent<LiasseCheckerFormProps> = function ({
    createdLiasses,
    getStatusIdentifier,
    getFichierContent,
    handleSubmit,
    isSubmitting,
}) {
    const [documentPreview, setDocumentPreview] = useState<DocPreview>();
    const [filesToRemove, setFilesToRemove] = useState<FproDosDocFileJsonldDocumentfileRead[]>([]);

    const { palette } = useTheme();

    /**
     * Check or uncheck file by updating filesToRemove state
     *
     * @param file FproDosDocFileJsonldDocumentfileRead
     */
    const handleCheckFile = (file: FproDosDocFileJsonldDocumentfileRead) => {
        if (filesToRemove.some((fileToRemove) => fileToRemove.id === file.id)) {
            setFilesToRemove((filesState) =>
                filesState.filter((fileState) => fileState.id !== file.id),
            );
        } else {
            setFilesToRemove((filesState) => [...filesState, file]);
        }
    };
    const handleRemoveCreatedDocuments = () => {
        handleSubmit(filesToRemove);
    };

    // CreatedFiles to DocItem[]
    // The files are not allways allready dispatch to the useDossierFile hook, so we have to create a specfic showHandler
    const dosDocFiletoDisplayedDocument = (file: FproDosDocFileJsonldDocumentfileRead): DocItem => {
        const statusId = file.statut?.id ? file.statut.id : 0;
        let label = file.typeDocDossier?.libelle as string;
        label = label.charAt(0).toUpperCase() + label.slice(1);
        const fileName = file.fichier as string;
        const fichierId = file.id as number;
        const documentId = file.typeDocDossier?.id as number;
        const fileExtension = getFileExtension(fileName) as DocumentExtension;
        const documentTypeId = file.typeDocDossier?.document?.id;

        return {
            id: documentId,
            fichierId,
            type: fileExtension,
            label,
            fileName,
            status: getStatusIdentifier(statusId),
            feedback: file.statut?.libelle as string | undefined,
            uploadable: false,
            uploading: false,
            documentTypeId,
            file,
            itemActions: {
                onShow: async (e) => {
                    if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    try {
                        const fileContent = await getFichierContent(fichierId as number);
                        setDocumentPreview({
                            fileName,
                            fileType: fileExtension,
                            content: fileContent.data as unknown as Blob,
                        });
                    } catch (err) {
                        console.log(err);
                    }
                },
                showTooltip: 'Afficher le document',
            },
        };
    };

    const viewer = useMemo(() => {
        if (!documentPreview) {
            return null;
        }
        const fileURL = URL.createObjectURL(
            new Blob([documentPreview.content as unknown as string], {
                type: 'application/pdf',
            }),
        );
        // Only pdf in pdf Viewer
        return (
            <PdfViewer
                title={documentPreview.fileName}
                pdfUrl={fileURL}
                showTitle={false}
                defaultPage={1}
            />
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentPreview?.fileName, documentPreview?.content]);

    return (
        <Box sx={{ width: '100%', height: '75vh', overflow: 'auto' }}>
            {createdLiasses && (
                <Box>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                        <Box
                            sx={{
                                width: '50%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            <Typography variant="l2dh4">Vérification de documents</Typography>

                            <Box sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                                {createdLiasses.map((createdLiasse) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                        }}
                                    >
                                        {/* Original file */}
                                        {createdLiasse.originalFile && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px',
                                                }}
                                            >
                                                <Typography variant="l2dh6">
                                                    Fichier originel
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <DocumentItem
                                                        label={createdLiasse.originalFile.name}
                                                        uploadable={false}
                                                        uploading={false}
                                                        type={
                                                            getFileExtension(
                                                                createdLiasse.originalFile.name,
                                                            ) as DocumentExtension
                                                        }
                                                        itemActions={{
                                                            onShow: async (e) => {
                                                                if (e) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                }
                                                                if (createdLiasse.originalFile) {
                                                                    setDocumentPreview({
                                                                        fileName:
                                                                            createdLiasse
                                                                                .originalFile.name,
                                                                        content:
                                                                            createdLiasse.originalFile,
                                                                    });
                                                                }
                                                            },
                                                            showTooltip: 'Afficher le document',
                                                        }}
                                                        sx={{ width: '100%' }}
                                                        labelSx={{
                                                            display: 'block',
                                                            minWidth: '15rem',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Created files */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                            }}
                                        >
                                            <Typography variant="l2dh6">Fichiers créés</Typography>
                                            {createdLiasse.createdFiles.map((file) => (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            !filesToRemove.some(
                                                                (fileToRemove) =>
                                                                    fileToRemove.id === file.id,
                                                            )
                                                        }
                                                        onChange={() => handleCheckFile(file)}
                                                    />
                                                    <DocumentItem
                                                        {...dosDocFiletoDisplayedDocument(file)}
                                                        sx={{ width: '100%' }}
                                                        labelSx={{
                                                            display: 'block',
                                                            minWidth: '15rem',
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            {/* Footer */}
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'end',
                                        gap: '16px',
                                    }}
                                >
                                    <Button
                                        disabled={isSubmitting}
                                        onClick={handleRemoveCreatedDocuments}
                                        sx={{
                                            padding: '8px 20px',
                                            fontSize: '16px',
                                            background: palette.primary.dark,
                                            ':hover': {
                                                background: palette.primary.main,
                                            },
                                            transition: 'all ease-in-out 300ms',
                                        }}
                                        variant="contained"
                                        key="publish-bodacc-btn"
                                    >
                                        Valider les documents générés
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: '50%',
                                maxHeight: '70vh',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                            }}
                        >
                            {viewer}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LiasseCheckerForm;
